import type { TThemeTokens } from '@modern/theme/theme-variables/theme-variables.types';

const VARIABLE_PREFIX = '--m';

const toVariableName = (path: readonly (string | number)[]) =>
  [VARIABLE_PREFIX, ...path].join('-');

const isPlainObject = (node: unknown): node is Record<string, unknown> => {
  if (!node || typeof node !== 'object') return false;

  const prototype: unknown = Object.getPrototypeOf(node);

  return prototype === Object.prototype || prototype === null;
};

const walk = (
  node: unknown,
  path: readonly (string | number)[],
  visit: (value: string, path: readonly (string | number)[]) => unknown,
): unknown => {
  if (typeof node === 'string') return visit(node, path);

  if (Array.isArray(node)) {
    return node.map((entry, index) => walk(entry, [...path, index], visit));
  }

  if (isPlainObject(node)) {
    return Object.fromEntries(
      Object.entries(node).map(([key, entry]) => [
        key,
        walk(entry, [...path, key], visit),
      ]),
    );
  }

  return node;
};

export const toThemeReferences = <T extends TThemeTokens>(tokens: T): T =>
  walk(tokens, [], (_value, path) => `var(${toVariableName(path)})`) as T;

export const toThemeDeclarations = (tokens: TThemeTokens) => {
  const declarations: string[] = [];

  walk(tokens, [], (value, path) =>
    declarations.push(`${toVariableName(path)}: ${value};`),
  );

  return declarations.join('\n');
};

const REFERENCE_PATTERN = /var\((--m-[\w-]+)\)/g;

export const toThemeValues = (
  tokens: TThemeTokens,
): ReadonlyMap<string, string> => {
  const values = new Map<string, string>();

  walk(tokens, [], (value, path) => values.set(toVariableName(path), value));

  return values;
};

const resolve = (
  node: unknown,
  values: ReadonlyMap<string, string>,
): unknown => {
  if (typeof node === 'string') {
    return node.includes('var(--m-')
      ? node.replaceAll(
          REFERENCE_PATTERN,
          (reference, name: string) => values.get(name) ?? reference,
        )
      : node;
  }

  if (Array.isArray(node)) {
    let resolved: unknown[] | undefined;

    node.forEach((entry: unknown, index) => {
      const next = resolve(entry, values);

      if (next === entry && !resolved) return;

      resolved ??= node.slice(0, index);
      resolved.push(next);
    });

    return resolved ?? node;
  }

  if (isPlainObject(node)) {
    let resolved: Record<string, unknown> | undefined;

    for (const [key, entry] of Object.entries(node)) {
      const next = resolve(entry, values);

      if (next !== entry) resolved ??= { ...node };
      if (resolved) resolved[key] = next;
    }

    return resolved ?? node;
  }

  return node;
};

export const resolveThemeReferences = <T>(
  node: T,
  values: ReadonlyMap<string, string>,
): T => resolve(node, values) as T;
